import { ConnectedBuyFormatCurrency } from '@buy/connectedComponents'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { BillingPeriod } from '@common/constants'
import { getPrice } from '@buy/utils/volume-components-helpers'
import { getUsagePriceType, isFlatRateUsagePrice, isPayGoUsagePrice } from '@buy/utils/get-usage-price-values'
import styles from '../../connectedComponents/OrderSummary/order-summary.module.css'
import { BillingTenureValue } from '../BillingPeriodSwitch'

interface AdditionalLinesSummaryProps {
    line: OrderSummaryLine
    isAnnual: boolean
    showSubTotal?: boolean
    isAddon?: boolean
    addonText?: string
    setSubTotal?: (value: number) => void
    subTotal?: number
    cleanUpFunc?: (unit: string) => void
    billingTenure: BillingTenureValue
}

export const AdditionalLinesSummary: FunctionComponent<AdditionalLinesSummaryProps> = ({
    line,
    isAnnual,
    showSubTotal = true,
    isAddon,
    addonText = '',
    setSubTotal,
    subTotal,
    cleanUpFunc,
    billingTenure,
}) => {
    useEffect(() => {
        return () => {
            cleanUpFunc?.(line.unit!)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const [isPayAsYouGo, setIsPayAsYouGo] = useState(false)

    useEffect(() => {
        if (isAddon) {
            const usagePriceType = getUsagePriceType(line.allPrices, line.usagePrice)
            if (isPayGoUsagePrice(usagePriceType)) setIsPayAsYouGo(true)
        }
    }, [isAddon, line.usagePrice, line.allPrices])

    useEffect(() => {
        if (!isAddon) {
            const price =
                getPrice(1, line.allPrices[isAnnual ? BillingPeriod.ANNUAL : billingTenure.leftOption!]) *
                line.multiplier *
                (isAnnual ? 12 : 1)
            setSubTotal?.(price)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [line, isAnnual, billingTenure, isAddon])

    const getLinePrice = () => {
        const price =
            getPrice(1, line.allPrices[isAnnual ? BillingPeriod.ANNUAL : billingTenure.leftOption!]) *
            (isAnnual ? 12 : 1)

        if (isAddon) {
            const usagePriceType = getUsagePriceType(line.allPrices, line.usagePrice)
            if (isPayGoUsagePrice(usagePriceType)) return line.usagePrice!
            if (isFlatRateUsagePrice(usagePriceType)) return getPrice(1, line.allPrices[BillingPeriod.MONTHLY])
            return price
        }
        return price
    }

    return (
        <>
            <div key={line.key} data-qat="addon-GTRStandardAddAgentNamed-summary">
                <div className={styles.row}>
                    <span className={`${styles['item-sub-title']} ${styles.total}`} data-qat="line-item-name">
                        {`${line.title} ${addonText}`}
                    </span>
                    {showSubTotal && (
                        <span className={`${styles.item}`} data-qat="line-item-subtotal">
                            <ConnectedBuyFormatCurrency
                                value={
                                    subTotal ||
                                    getPrice(
                                        1,
                                        line.allPrices[isAnnual ? BillingPeriod.ANNUAL : billingTenure.leftOption!]
                                    ) *
                                        line.multiplier *
                                        (isAnnual ? 12 : 1)
                                }
                            />
                        </span>
                    )}
                </div>

                <div className={styles.row}>
                    <span className={styles.small} data-qat="line-item-sub-labels">
                        <em>
                            <ConnectedBuyFormatCurrency value={getLinePrice()} />
                            {isPayAsYouGo ? (
                                <>
                                    &nbsp;
                                    {line.usagePriceLabel}
                                </>
                            ) : (
                                <>
                                    &nbsp;x&nbsp;
                                    {line.quantity || 0} {isAddon ? line.unit : line.title!.toLowerCase()}
                                </>
                            )}
                        </em>
                        <br />
                    </span>
                </div>
            </div>
        </>
    )
}
